/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import creditCardType, { types as CardType } from 'credit-card-type';
import { EyeIcon, Input, Select } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { formatCardNumber, formatDniNumber, TDC_ACTIONS } from './reducer';
import { months, years } from '../../../../static';
import { useNotify, useUser } from '../../../../hooks';
import { PAYMENT_BY_TDC } from '../../../../graphql/mutation';
import { validateTdcForm } from '../../../../lib/fomrValidations';
import {
  PaymentMethodData,
  PaymentResponse,
  TDCPayment,
} from '../../../../models';
import { getPublicIP } from '../../../../lib';
import PaymentMethodFooter from './PaymentMethodFooter';
import Button from '../../Button/Button';

interface TarjetaFormProps {
  paymentData: PaymentMethodData;
  setFormInfo: React.Dispatch<
    React.SetStateAction<{ type: string; dispatch: PaymentMethodData }>
  >;
  amount: number;
  setPhase: React.Dispatch<React.SetStateAction<number>>;
  createOrder: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    paymentIds: Array<string>
  ) => Promise<void>;
}

function TDCForm({
  paymentData,
  setFormInfo,
  amount,
  setPhase,
  createOrder,
}: TarjetaFormProps) {
  const [tdcStateData, setTdcStateData] = React.useState<TDCPayment>({
    cardname: '',
    dni: '',
    typetdc: '',
    cardnumber: '',
    expirationdate: '',
    cvc: '',
    amount,
  });
  const [stateDate, setStateDate] = React.useState<{
    month: string;
    year: string;
  }>({
    month: '',
    year: '',
  });
  const [showCVC, setShowCVC] = React.useState<boolean>(false);
  const [cardImage, setCardImage] = React.useState<string>('');
  const notify = useNotify();
  const [user] = useUser();

  const [paymentByTdc, { loading }] = useMutation<{
    paymentByTDC: PaymentResponse;
  }>(PAYMENT_BY_TDC);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Filter input value
    const filteredValue = inputLimitCharacter(name, value);
    setTdcStateData((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: filteredValue,
    }));
  };

  const handleSelectDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setStateDate((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: value,
    }));
  };

  function filterCreditCardTypes(cardNumber: string) {
    // Validate card number length
    if (!cardNumber) {
      setTdcStateData((prev) => ({
        ...prev,
        typetdc: 'N/A',
      }));
      setCardImage('');
      return;
    }

    // Catch card type using the card number
    const response = creditCardType(cardNumber).filter(
      (card) => card.type === CardType.MASTERCARD || card.type === CardType.VISA
    );

    // Validate card type using response
    if (response.length > 0) {
      const cardType = response[0].type;
      setTdcStateData((prev) => ({
        ...prev,
        typetdc: cardType,
      }));
      getCardType(cardType);
      return;
    }
    setTdcStateData((prev) => ({
      ...prev,
      typetdc: 'N/A',
    }));
    setCardImage('');
  }

  function getCardType(cardType: string) {
    switch (cardType) {
      case CardType.MASTERCARD:
        setCardImage(
          'https://mtf.mastercard.co.za/content/dam/public/mastercardcom/mea/za/logos/mc-logo-52.svg'
        );
        break;
      case CardType.VISA:
        setCardImage(
          'https://cdn.visa.com/v2/assets/images/logos/visa/blue/logo.png'
        );
        break;
      default:
        setCardImage('');
        break;
    }
  }

  const makePayement = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const userIp = await getPublicIP();

    try {
      // Validate form
      const error = validateTdcForm(tdcStateData);
      if (error) return notify(error, 'error');

      const { data } = await paymentByTdc({
        variables: {
          data: {
            keyId: paymentData.keyId,
            publicKeyId: paymentData.publicKeyId,
            paymentMethodId: paymentData.paymentId,
            userId: user._id,
            amount: tdcStateData.amount,
            description: `'Pago: Monto: ${tdcStateData.amount}, User: ${tdcStateData.cardname}, DNI: ${tdcStateData.dni}, paymentMethodId: 66e0876ff92d099ca79ec33f`,
            cardHolder: tdcStateData.cardname,
            cardHolderId: tdcStateData.dni,
            cardNumber: tdcStateData.cardnumber,
            CVC: tdcStateData.cvc,
            expirationDate: tdcStateData.expirationdate,
            IP: userIp,
          },
        },
      });

      if (data?.paymentByTDC?.paymentResult === 'approved') {
        notify('Se realizo el pago con exito', 'success');
        setFormInfo({
          type: 'paymentSuccessful',
          dispatch: {
            paymentId: '',
            keyId: '',
            publicKeyId: '',
            bank: '',
            dniType: '',
            dni: '',
            phone: '',
            email: '',
          },
        });
        setPhase((prev) => prev + 1);
        setTimeout(() => {
          createOrder(e, [data?.paymentByTDC?._payment?._id]);
        }, 2000);
      } else {
        notify(
          'Ha ocurrido un error al hacer el pago, intente de nuevo',
          'error'
        );
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  React.useEffect(() => {
    if (stateDate.year !== '' && stateDate.month !== '') {
      // Validate expiration date
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const selectedMonth = parseInt(stateDate.month);
      const currentYear = currentDate.getFullYear();
      const selectedYear = parseInt(stateDate.year);
      if (selectedMonth < currentMonth && selectedYear === currentYear) {
        notify('La fecha de vencimiento no es válida', 'error');
        return;
      }
      setTdcStateData((prevState) => ({
        ...prevState,
        expirationdate: `${stateDate.month}/${stateDate.year}`,
      }));
    }
  }, [stateDate]);

  React.useEffect(() => {
    filterCreditCardTypes(tdcStateData.cardnumber);
  }, [tdcStateData.cardnumber]);

  React.useEffect(() => {
    setTdcStateData((prevState) => ({
      ...prevState,
      amount,
    }));
  }, [amount]);

  return (
    <section className="min-w-[90%] min-h-[33vh] relative px-6 py-3 border rounded-lg space-y-5">
      <div className="flex flex-row justify-between items-center">
        <div className="w-full">
          <h1 className="text-xl text-text-gray font-semibold">
            Pago con tarjeta
          </h1>
        </div>
        <div className="w-[30%]">
          <Button
            className="mt-5 relative z-20 bg-white border border-color-primary-300 text-color-primary-text-300  text-sm sm:text-base"
            onClick={(e) => {
              e.preventDefault();
              setFormInfo({
                type: '',
                dispatch: {
                  paymentId: '',
                  keyId: '',
                  publicKeyId: '',
                  bank: '',
                  dniType: '',
                  dni: '',
                  phone: '',
                  email: '',
                },
              });
              setPhase((prev) => prev - 1);
            }}
          >
            Volver
          </Button>
        </div>
      </div>
      <div className="w-full flex flex-col gap-5">
        <div className="w-full flex md:flex-row flex-col gap-5">
          <div className="w-full">
            <Input
              name={TDC_ACTIONS.CARDNAME}
              value={tdcStateData.cardname}
              label="Nombre del titular"
              type="text"
              placeholder="Ingrese el nombre del titular"
              onChange={handleChange}
              maxLength={50}
              required
            />
          </div>
          <div className="w-full">
            <Input
              name={TDC_ACTIONS.DNI}
              value={formatDniNumber(tdcStateData.dni)}
              label="Número de cédula"
              type="text"
              placeholder="Ingrese el número de cédula"
              onChange={handleChange}
              minLength={7}
              maxLength={10}
              required
            />
          </div>
        </div>
        <div className="w-full flex md:flex-row flex-col gap-5">
          <div className="w-full relative">
            <Input
              name={TDC_ACTIONS.CARDNUMBER}
              value={formatCardNumber(tdcStateData.cardnumber)}
              label="Número de tarjeta"
              type="text"
              placeholder="Número de tarjeta (Visa o Mastercard)"
              onChange={handleChange}
              minLength={18}
              maxLength={19}
              required
            />
            <div>
              {cardImage !== 'N/A' && cardImage !== '' ? (
                <img
                  className="w-6 absolute right-1 top-3 transform -translate-y-1/2"
                  src={cardImage}
                  alt="card_logo"
                />
              ) : (
                <span
                  className="w-6 scale-50 absolute right-1 top-3 transform -translate-y-1/2"
                  role="img"
                  aria-label="Invalid card"
                >
                  ❌
                </span>
              )}
            </div>
          </div>
          <div className="w-full">
            <div className="flex flex-row justify-center items-center space-x-2">
              <div className="w-full ">
                <Input
                  name={TDC_ACTIONS.CVC}
                  label="CVC"
                  value={tdcStateData.cvc}
                  type={showCVC ? 'text' : 'password'}
                  placeholder="Ingrese el CVC"
                  onChange={handleChange}
                  minLength={3}
                  maxLength={3}
                  required
                />
              </div>
              <div className="relative">
                <EyeIcon
                  className="w-5 h-5 cursor-pointer absolute right-5"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowCVC((prev) => !prev);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full flex flex-col">
          <span className="text-gray-300 text-sm font-arial font-extrabold tracking-wider ">
            Fecha de vencimiento
          </span>
          <div className="flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <Select
                name="month"
                value={stateDate.month}
                label="Mes"
                onChange={handleSelectDateChange}
                required
              >
                <option value="" disabled>
                  Mes
                </option>
                {months.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.name} ({option.value})
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-full">
              <Select
                name="year"
                value={stateDate.year}
                label="Año"
                onChange={handleSelectDateChange}
                required
              >
                <option value="" disabled>
                  Año
                </option>
                {years.map((option) => (
                  <option value={option} key={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
        <div className="w-full flex flex-row justify-center items-center mt-5">
          <Button
            className="w-1/2 bg-color-primary-300 hover:bg-color-primary-100 border-solid border border-cold-500 text-system-100 justify-center px-3 font-normal"
            onClick={(e) => makePayement(e)}
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Procesar pago'}
          </Button>
        </div>
        <PaymentMethodFooter />
      </div>
    </section>
  );
}

export default TDCForm;

function inputLimitCharacter(name: string, value: string) {
  switch (name) {
    case TDC_ACTIONS.CARDNAME:
      return value.replace(/[^a-zA-ZñÑ\s]/g, '');
    case TDC_ACTIONS.DNI:
      return value.replace(/[^0-9]/g, '');
    case TDC_ACTIONS.CARDNUMBER:
      return value.replace(/[^0-9]/g, '');
    case TDC_ACTIONS.CVC:
      return value.replace(/[^0-9]/g, '');
    default:
      return value;
  }
}
